import { ConversationStatus, ConversationType, DataRequestChannel, } from "@loanlink-nl/drizzle";
import { AgencyMismatch, AuthenticationRequired, EditingOwnerOfDossierNotAllowed, InsufficientPermissions, ResourceNotFound, UserCantBeOwner, VersionNotFound, } from "@loanlink-nl/shared/errors";
import { defineMutator, defineMutators, } from "@rocicorp/zero";
import * as jsondiffpatch from "jsondiffpatch";
import * as z from "zod";
import { zql } from "./schema.js";
export const applyDataRequestArgsSchema = z.object({
    id: z.string(),
    appliedAt: z.number(),
});
export const createInformatieAanvraagArgsSchema = z.object({
    id: z.string(),
    hdnDossierId: z.string(),
    dossierId: z.string(),
    agencyId: z.string(),
    existingMortgageId: z.string().optional(),
    createdAt: z.number(),
    requestTraceNr: z.string(),
    providerCode: z.string(),
    content: z.object({
        mortgageNumber: z.string(),
        lenderCode: z.string(),
        customers: z.array(z.object({
            lastname: z.string(),
            birthDate: z.string(),
        })),
    }),
});
export const setInformatieAanvraagExistingMortgageArgsSchema = z.object({
    id: z.string(),
    existingMortgageId: z.string().nullable(),
});
export const createDataRequestArgsSchema = z.object({
    id: z.string(),
    customerId: z.string(),
    dossierId: z.string(),
    agencyId: z.string(),
    createdAt: z.number(),
    channel: z.enum(DataRequestChannel),
    hdnDossierId: z.string().optional(),
    requestTraceNr: z.string().optional(),
});
export const createConversationArgsSchema = z.object({
    id: z.string(),
    title: z.string().optional(),
    type: z.enum(ConversationType),
    createdAt: z.number().optional(),
    agencyId: z.string(),
    dossierId: z.string().nullable().optional(),
    filename: z.string().optional(),
    contentType: z.string().optional(),
    byteSize: z.number().optional(),
    recordingId: z.string().optional(),
});
export const createDossierFromConversationArgsSchema = z.object({
    id: z.string(),
    name: z.string().min(1),
    conversationId: z.string(),
    createdAt: z.number(),
});
export const connectDocumentArgsSchema = z.object({
    documentId: z.string(),
    id: z.string(),
    actionId: z.string(),
});
export const disconnectDocumentArgsSchema = z.object({
    id: z.string(),
    actionId: z.string(),
});
export const sendDocumentArgsSchema = z.object({
    id: z.string(),
    documentId: z.string(),
    actionId: z.string(),
});
export const setStatusArgsSchema = z.object({
    id: z.string(),
    status: z
        .enum(ConversationStatus)
        .and(z.enum(["ready_to_transcribe", "ready_to_summarize", "errored"])),
});
export const upsertConnectionArgsSchema = z.object({
    agencyId: z.string(),
    lenderCode: z.string(),
    sendMethod: z.enum(["direct", "service_provider"]),
    serviceProviderId: z.string().optional(),
    connectionNumber: z.string().optional(),
});
export const rerunInboundEmailHandlerArgsSchema = z.object({
    inboundEmailId: z.string(),
});
export const analyzeInterestSheetArgsSchema = z.object({
    interestSheetId: z.string(),
});
/**
 * Checks if the authenticated user can edit a dossier.
 * Returns the user if they have access, throws otherwise.
 */
async function assertCanEditDossier(tx, ctx, dossierId) {
    if (!ctx) {
        throw new AuthenticationRequired();
    }
    const user = await tx.run(zql.appUser
        .where("firebaseId", ctx.sub)
        .where((q) => q.and(q.cmp("role", "IN", ["intermediary", "admin"]), q.exists("agency", (q) => q.whereExists("dossiers", (q) => q.where("id", dossierId)))))
        .one());
    if (!user?.agencyId) {
        throw new InsufficientPermissions("edit dossier");
    }
    return user;
}
async function assertIsIntermediaryOrAdmin(tx, ctx) {
    if (!ctx) {
        throw new AuthenticationRequired();
    }
    const user = await tx.run(zql.appUser
        .where("firebaseId", ctx.sub)
        .where("role", "IN", ["intermediary", "admin"])
        .one());
    if (!user) {
        throw new InsufficientPermissions("rerun inbound email handling");
    }
    return user;
}
export const mutators = defineMutators({
    conversationTable: {
        create: defineMutator(createConversationArgsSchema, async ({ tx, ctx, args: { id, title, type, createdAt, agencyId, dossierId, filename, contentType, byteSize, recordingId, }, }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", agencyId)
                .where("role", "IN", ["intermediary", "admin", "transcription"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("create conversation for this agency");
            }
            if (dossierId) {
                const dossier = await tx.run(zql.dossier
                    .where("id", dossierId)
                    .where("agencyId", agencyId)
                    .one());
                if (!dossier) {
                    throw new ResourceNotFound("Dossier", dossierId);
                }
            }
            if (type === "upload" &&
                filename &&
                contentType &&
                byteSize &&
                recordingId) {
                await tx.mutate.dossierDocument.insert({
                    id: recordingId,
                    agencyId,
                    filename,
                    byteSize,
                    contentType,
                    type: "meeting_recording",
                });
            }
            await tx.mutate.conversationTable.insert({
                id,
                title,
                type,
                createdAt,
                createdBy: userHasAccess.id,
                agencyId,
                dossierId: dossierId ?? null,
                recordingId,
            });
        }),
        setStatus: defineMutator(setStatusArgsSchema, async ({ tx, ctx, args: { id, status } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const conversation = await tx.run(zql.conversationTable
                .where("id", id)
                .where("status", "pending")
                .one());
            if (!conversation) {
                throw new Error("Conversation not found");
            }
            if (status === "ready_to_transcribe" &&
                conversation.type !== "upload") {
                throw new Error("Can only set status to ready_to_transcribe when conversation is an upload");
            }
            if (status === "ready_to_summarize" &&
                conversation.type !== "recording") {
                throw new Error("Can only set status to ready_to_summarize when conversation is a recording");
            }
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", conversation.agencyId)
                .where("role", "IN", ["intermediary", "admin", "transcription"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("update conversation status for this agency");
            }
            await tx.mutate.conversationTable.update({
                id,
                status,
            });
        }),
        setTitle: defineMutator(z.object({
            id: z.string(),
            title: z.string().max(255).nullable(),
        }), async ({ tx, ctx, args: { id, title } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const conversation = await tx.run(zql.conversationTable.where("id", id).one());
            if (!conversation) {
                throw new ResourceNotFound("Conversation", id);
            }
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", conversation.agencyId)
                .where("role", "IN", ["intermediary", "admin", "transcription"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("update conversation title for this agency");
            }
            await tx.mutate.conversationTable.update({
                id,
                title,
            });
        }),
        setDossier: defineMutator(z.object({
            id: z.string(),
            dossierId: z.string().nullable(),
        }), async ({ tx, ctx, args: { id, dossierId } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const conversation = await tx.run(zql.conversationTable.where("id", id).one());
            if (!conversation) {
                throw new ResourceNotFound("Conversation", id);
            }
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", conversation.agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("link conversation to dossier for this agency");
            }
            if (dossierId !== null) {
                const dossier = await tx.run(zql.dossier
                    .where("id", dossierId)
                    .where("agencyId", conversation.agencyId)
                    .one());
                if (!dossier) {
                    throw new ResourceNotFound("Dossier", dossierId);
                }
            }
            await tx.mutate.conversationTable.update({ id, dossierId });
        }),
        saveTranscription: defineMutator(z.object({
            id: z.string(),
            detailId: z.string(),
            transcriptionResult: z.array(z.any()),
        }), async ({ tx, ctx, args: { id, detailId, transcriptionResult } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const conversation = await tx.run(zql.conversationTable.where("id", id).one());
            if (!conversation) {
                throw new Error("Conversation not found");
            }
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", conversation.agencyId)
                .where("role", "IN", ["intermediary", "admin", "transcription"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("save transcription for this agency");
            }
            await tx.mutate.conversationDetail.upsert({
                id: detailId,
                conversationId: id,
                agencyId: conversation.agencyId,
                transcriptionResult: { results: transcriptionResult },
            });
        }),
    },
    dataRequestTable: {
        create: defineMutator(createDataRequestArgsSchema, async ({ tx, ctx, args: { id, customerId, dossierId, agencyId, createdAt, channel, hdnDossierId, requestTraceNr, }, }) => {
            // Ensure user is authenticated
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            // Check if dossier exists
            const dossier = await tx.run(zql.dossier.where("id", dossierId).one());
            if (!dossier) {
                throw new ResourceNotFound("Dossier", dossierId);
            }
            // Verify user has access to the agency
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", agencyId)
                .where("role", "IN", ["intermediary", "admin", "transcription"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("create HDN data request for this agency");
            }
            // Verify the dossier belongs to the same agency
            if (dossier.agencyId !== agencyId) {
                throw new AgencyMismatch();
            }
            if (channel === "hdn") {
                if (!hdnDossierId) {
                    throw new Error("hdnDossierId is required for hdn channel");
                }
                if (!requestTraceNr) {
                    throw new Error("requestTraceNr is required for hdn channel");
                }
                await tx.mutate.hdnDossierTable.insert({
                    id: hdnDossierId,
                    receiverCode: "HDN",
                    hdnRequestTraceNr: requestTraceNr,
                    dossierId,
                    agencyId,
                    createdAt,
                    kind: "data_request",
                });
            }
            await tx.mutate.dataRequestTable.insert({
                id,
                customerId,
                hdnDossierId,
                dossierId,
                agencyId,
                createdAt,
                createdBy: userHasAccess.id,
                channel,
            });
        }),
        applyData: defineMutator(applyDataRequestArgsSchema, async ({ tx, ctx, args: { id, appliedAt } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const dataRequest = await tx.run(zql.dataRequestTable.where("id", id).one());
            if (!dataRequest) {
                throw new Error("Data request not found");
            }
            // Verify user has access to the agency
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", dataRequest.agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("create HDN data request for this agency");
            }
            await tx.mutate.dataRequestTable.update({
                id,
                appliedAt,
                appliedBy: userHasAccess.id,
            });
        }),
    },
    inboundEmailTable: {
        rerunHandler: defineMutator(rerunInboundEmailHandlerArgsSchema, async ({ tx, ctx }) => {
            await assertIsIntermediaryOrAdmin(tx, ctx);
        }),
    },
    interestSheet: {
        analyze: defineMutator(analyzeInterestSheetArgsSchema, async ({ tx, ctx }) => {
            await assertIsIntermediaryOrAdmin(tx, ctx);
        }),
    },
    agency: {
        updateFinancingDefaults: defineMutator(z.object({
            agencyId: z.string(),
            financingDefaults: z.object({
                notarisLevering: z.number().optional(),
                notarisHypotheek: z.number().optional(),
                taxatiekosten: z.number().optional(),
                kostenHypotheekadvies: z.number().optional(),
            }),
        }), async ({ tx, ctx, args }) => {
            if (!ctx)
                throw new AuthenticationRequired();
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", args.agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("update financing defaults for this agency");
            }
            await tx.mutate.agency.update({
                id: args.agencyId,
                financingDefaults: args.financingDefaults,
            });
        }),
    },
    agencyServiceProviderConnection: {
        upsertConnection: defineMutator(z.object({
            agencyId: z.string(),
            serviceProviderId: z.string(),
            connectionNumber: z.string().optional(),
        }), async ({ tx, ctx, args: { agencyId, serviceProviderId, connectionNumber }, }) => {
            const normalizedConnectionNumber = connectionNumber?.trim() || null;
            // Ensure user is authenticated
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const serviceProvider = await tx.run(zql.hdnServiceProvider
                .where("id", serviceProviderId)
                .related("availableProviders", (cb) => cb.related("connections", (cb) => cb
                .where("agencyId", agencyId)
                .where((q) => q.or(q.cmp("connectionNumber", "IS NOT", null), q.cmp("serviceProviderId", "IS NOT", null)))))
                .one());
            if (!serviceProvider) {
                throw new ResourceNotFound("ServiceProvider", serviceProviderId);
            }
            // Verify user has access to the agency
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("manage service provider connections for this agency");
            }
            await tx.mutate.agencyServiceProviderConnection.upsert({
                agencyId,
                serviceProviderId,
                connectionNumber: normalizedConnectionNumber,
                connectedBy: userHasAccess.id,
                createdAt: Date.now(),
            });
            if (normalizedConnectionNumber) {
                const providersWithoutConnections = serviceProvider.availableProviders.filter((provider) => provider.connections.length === 0);
                if (providersWithoutConnections.length > 0) {
                    await Promise.all(providersWithoutConnections.map((provider) => tx.mutate.hdnLenderConnection.upsert({
                        agencyId,
                        lenderCode: provider.name,
                        serviceProviderId,
                    })));
                }
            }
            else {
                const providersWithConnections = serviceProvider.availableProviders.filter((provider) => provider.connections[0]?.serviceProviderId ===
                    serviceProviderId);
                if (providersWithConnections.length > 0) {
                    const availableServiceProviders = await tx.run(zql.hdnServiceProvider
                        .whereExists("connections", (q) => q.where((q) => q.and(q.cmp("agencyId", agencyId), q.cmp("connectionNumber", "IS NOT", null))))
                        .related("availableProviders")
                        .orderBy("name", "asc"));
                    const fallbackServiceProviderByLenderCode = new Map(availableServiceProviders
                        .filter((provider) => provider.id !== serviceProviderId)
                        .flatMap((provider) => provider.availableProviders.map((availableProvider) => [
                        availableProvider.name,
                        provider.id,
                    ])));
                    await Promise.all(providersWithConnections.map(async (provider) => {
                        const fallbackServiceProviderId = fallbackServiceProviderByLenderCode.get(provider.name);
                        if (!fallbackServiceProviderId) {
                            await tx.mutate.hdnLenderConnection.delete({
                                agencyId,
                                lenderCode: provider.name,
                            });
                            return;
                        }
                        await tx.mutate.hdnLenderConnection.update({
                            agencyId,
                            lenderCode: provider.name,
                            serviceProviderId: fallbackServiceProviderId,
                            connectionNumber: null,
                            sendMethod: "service_provider",
                        });
                    }));
                }
            }
        }),
    },
    hdnRequestedDocument: {
        connect: defineMutator(connectDocumentArgsSchema, async ({ tx, ctx, args: { documentId, id, actionId } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const requestedDocument = await tx.run(zql.hdnRequestedDocument.where("id", id).one());
            if (!requestedDocument) {
                throw new Error("Requested document not found");
            }
            // Verify user has access to the agency
            let userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", requestedDocument.agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!userHasAccess) {
                const doc = await tx.run(zql.hdnRequestedDocument
                    .where("id", id)
                    .whereExists("loanApplication", (q) => q.whereExists("dossier", (q) => q.whereExists("dossierUser", (q) => q.whereExists("appUser", (q) => q.where("firebaseId", ctx.sub)))))
                    .one());
                if (!doc) {
                    throw new InsufficientPermissions("connect document for this agency");
                }
                userHasAccess = await tx.run(zql.appUser.where("firebaseId", ctx.sub).one());
                if (!userHasAccess) {
                    throw new InsufficientPermissions("connect document for this agency");
                }
            }
            await tx.mutate.hdnRequestedDocumentAction.insert({
                documentId,
                requestedDocumentId: id,
                agencyId: requestedDocument.agencyId,
                actionType: "link",
                changedBy: userHasAccess.id,
                id: actionId,
            });
            await tx.mutate.hdnRequestedDocument.update({
                id,
                linkedDocumentActionId: actionId,
            });
        }),
        disconnect: defineMutator(disconnectDocumentArgsSchema, async ({ tx, ctx, args: { id, actionId } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const requestedDocument = await tx.run(zql.hdnRequestedDocument.where("id", id).one());
            if (!requestedDocument) {
                throw new Error("Requested document not found");
            }
            // Verify user has access to the agency
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", requestedDocument.agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("connect document for this agency");
            }
            await tx.mutate.hdnRequestedDocumentAction.insert({
                documentId: null,
                requestedDocumentId: id,
                agencyId: requestedDocument.agencyId,
                actionType: "link",
                changedBy: userHasAccess.id,
                id: actionId,
            });
            await tx.mutate.hdnRequestedDocument.update({
                id,
                linkedDocumentActionId: actionId,
                sentDocumentAt: null,
                sentDocumentActionId: null,
            });
        }),
        send: defineMutator(sendDocumentArgsSchema, async ({ tx, ctx, args: { id, actionId, documentId } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const requestedDocument = await tx.run(zql.hdnRequestedDocument
                .where("id", id)
                .related("linkedDocumentAction")
                .one());
            if (!requestedDocument) {
                throw new Error("Requested document not found");
            }
            const linkedId = requestedDocument.linkedDocumentAction?.documentId ?? null;
            if (!linkedId) {
                throw new Error("Cannot send: no linked document");
            }
            if (linkedId !== documentId) {
                throw new Error("Cannot send: provided documentId does not match the linked document");
            }
            // Verify user has access to the agency
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", requestedDocument.agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("connect document for this agency");
            }
            await tx.mutate.hdnRequestedDocumentAction.insert({
                documentId: documentId,
                requestedDocumentId: id,
                agencyId: requestedDocument.agencyId,
                actionType: "send",
                changedBy: userHasAccess.id,
                id: actionId,
            });
            await tx.mutate.hdnRequestedDocument.update({
                id,
                sentDocumentActionId: actionId,
            });
        }),
    },
    hdnLenderConnection: {
        upsertConnection: defineMutator(upsertConnectionArgsSchema, async ({ tx, ctx, args: { agencyId, lenderCode, serviceProviderId, connectionNumber, sendMethod, }, }) => {
            // Ensure user is authenticated
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("manage service provider connections for this agency");
            }
            await tx.mutate.hdnLenderConnection.upsert({
                agencyId,
                lenderCode,
                serviceProviderId: !serviceProviderId ? null : serviceProviderId,
                connectionNumber: !connectionNumber ? null : connectionNumber,
                sendMethod,
                createdAt: Date.now(),
            });
        }),
    },
    dossierVersionNotes: {
        upsert: defineMutator(z.object({
            versionId: z.string(),
            notes: z.string().nullable(),
        }), async ({ tx, ctx, args: { versionId, notes } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            // Verify version exists and user has access
            const version = await tx.run(zql.version
                .where("id", versionId)
                .whereExists("dossier", (q) => q.whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx.sub)
                .where("role", "IN", ["intermediary", "admin"]))))
                .one());
            if (!version) {
                throw new ResourceNotFound("Version", versionId);
            }
            const user = await tx.run(zql.appUser.where("firebaseId", ctx.sub).one());
            if (!user) {
                throw new AuthenticationRequired();
            }
            await tx.mutate.dossierVersionNotes.upsert({
                versionId,
                notes,
                changedBy: user.id,
                lastChanged: Date.now(),
            });
        }),
    },
    dossierDocument: {
        create: defineMutator(z.object({
            id: z.string(),
            dossierId: z.string(),
            filename: z.string(),
            contentType: z.string(),
            byteSize: z.number(),
            uploadedAt: z.number().optional(),
        }), async ({ tx, ctx, args: { id, dossierId, filename, contentType, byteSize, uploadedAt }, }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where((q) => q.or(q.and(q.cmp("role", "IN", ["intermediary", "admin"]), q.exists("agency", (q) => q.whereExists("dossiers", (q) => q.where("id", dossierId)))), q.and(q.cmp("role", "NOT IN", ["intermediary", "admin"]), q.exists("dossierUser", (q) => q.where("dossierId", dossierId)))))
                .one());
            if (!userHasAccess?.agencyId) {
                throw new InsufficientPermissions("create dossier document for this agency");
            }
            await tx.mutate.dossierDocument.insert({
                id,
                dossierId,
                agencyId: userHasAccess.agencyId,
                filename,
                contentType,
                byteSize,
                type: "user_upload",
                creationDate: Date.now(),
                uploadedAt: uploadedAt ?? null,
            });
        }),
        updateUploadedAt: defineMutator(z.object({
            id: z.string(),
            uploadedAt: z.number().optional(),
        }), async ({ tx, ctx, args: { id, uploadedAt } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where((q) => q.or(q.and(q.cmp("role", "IN", ["intermediary", "admin"]), q.exists("agency", (q) => q.whereExists("dossiers", (q) => q.whereExists("storedDocuments", (q) => q.where("id", id))))), q.and(q.cmp("role", "NOT IN", ["intermediary", "admin"]), q.exists("dossierUser", (q) => q.whereExists("dossier", (q) => q.whereExists("storedDocuments", (q) => q.where("id", id)))))))
                .one());
            if (!userHasAccess?.agencyId) {
                throw new InsufficientPermissions("create dossier document for this agency");
            }
            await tx.mutate.dossierDocument.update({
                id,
                uploadedAt,
            });
        }),
    },
    dossier: {
        setLastOpened: defineMutator(z.object({
            id: z.string(),
            lastOpened: z.number(),
        }), async ({ tx, ctx, args: { id, lastOpened } }) => {
            await assertCanEditDossier(tx, ctx, id);
            await tx.mutate.dossier.update({
                id,
                lastOpened,
            });
        }),
        setName: defineMutator(z.object({
            id: z.string(),
            name: z.string().min(1),
        }), async ({ tx, ctx, args: { id, name } }) => {
            await assertCanEditDossier(tx, ctx, id);
            await tx.mutate.dossier.update({
                id,
                name,
            });
        }),
        setOwner: defineMutator(z.object({
            id: z.string(),
            ownerId: z.string(),
        }), async ({ tx, ctx, args: { id, ownerId } }) => {
            await assertCanEditDossier(tx, ctx, id);
            // Get the dossier to check ownership
            const dossier = await tx.run(zql.dossier.where("id", id).one());
            if (!dossier) {
                throw new ResourceNotFound("Dossier", id);
            }
            // Can't set owner to current owner
            if (dossier.dossierOwner === ownerId) {
                throw new EditingOwnerOfDossierNotAllowed();
            }
            // Check if the new owner can be owner (same agency as dossier)
            const newOwner = await tx.run(zql.appUser
                .where("id", ownerId)
                .where("agencyId", dossier.agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!newOwner) {
                throw new UserCantBeOwner();
            }
            await tx.mutate.dossier.update({
                id,
                dossierOwner: ownerId,
            });
        }),
        update: defineMutator(z.object({
            id: z.string(),
            data: z.any(),
            modifiedAt: z.number(),
            mutationId: z.string(),
        }), async ({ tx, ctx, args: { id, data, modifiedAt, mutationId } }) => {
            const userHasAccess = await assertCanEditDossier(tx, ctx, id);
            const version = await tx.run(zql.version.where("dossierId", id).one());
            if (!version) {
                throw new VersionNotFound();
            }
            // ONLY update db if version data has changed
            const previousVersionData = version.versionData;
            const versionMutation = jsondiffpatch.diff(previousVersionData, data);
            if (!versionMutation) {
                return;
            }
            await tx.mutate.versionMutation.insert({
                id: mutationId,
                versionId: version.id,
                versionMutation,
                by: userHasAccess.id,
                mutationDate: modifiedAt,
            });
            await tx.mutate.version.update({
                id: version.id,
                versionData: data,
                lastEdited: modifiedAt,
            });
        }),
        createFromConversation: defineMutator(createDossierFromConversationArgsSchema, async ({ tx, ctx, args: { id, name, conversationId, createdAt } }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            // Verify the conversation exists and belongs to the user's agency
            const conversation = await tx.run(zql.conversationTable.where("id", conversationId).one());
            if (!conversation) {
                throw new ResourceNotFound("Conversation", conversationId);
            }
            // Verify user has access to the agency
            const user = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", conversation.agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!user) {
                throw new InsufficientPermissions("create dossier for this agency");
            }
            // Create the dossier
            await tx.mutate.dossier.insert({
                id,
                name,
                agencyId: conversation.agencyId,
                dossierOwner: user.id,
                createdBy: user.id,
                creationDate: createdAt,
                lastOpened: createdAt,
            });
            // Add user to dossierUser
            await tx.mutate.dossierUser.insert({
                userId: user.id,
                dossierId: id,
            });
            // Create version for the dossier
            const versionId = crypto.randomUUID();
            await tx.mutate.version.insert({
                id: versionId,
                dossierId: id,
            });
            // Link the conversation to the new dossier
            await tx.mutate.conversationTable.update({
                id: conversationId,
                dossierId: id,
            });
        }),
    },
    hdnInformatieAanvraagTable: {
        setExistingMortgage: defineMutator(setInformatieAanvraagExistingMortgageArgsSchema, async ({ tx, ctx, args: { id, existingMortgageId } }) => {
            const informatieAanvraag = await tx.run(zql.hdnInformatieAanvraagTable.where("id", id).one());
            if (!informatieAanvraag) {
                throw new ResourceNotFound("HdnInformatieAanvraag", id);
            }
            await assertCanEditDossier(tx, ctx, informatieAanvraag.dossierId);
            await tx.mutate.hdnInformatieAanvraagTable.update({
                id,
                existingMortgageId,
            });
        }),
        create: defineMutator(createInformatieAanvraagArgsSchema, async ({ tx, ctx, args: { id, hdnDossierId, dossierId, agencyId, existingMortgageId, createdAt, requestTraceNr, providerCode, content, }, }) => {
            if (!ctx) {
                throw new AuthenticationRequired();
            }
            const dossier = await tx.run(zql.dossier.where("id", dossierId).one());
            if (!dossier) {
                throw new ResourceNotFound("Dossier", dossierId);
            }
            const userHasAccess = await tx.run(zql.appUser
                .where("firebaseId", ctx.sub)
                .where("agencyId", agencyId)
                .where("role", "IN", ["intermediary", "admin"])
                .one());
            if (!userHasAccess) {
                throw new InsufficientPermissions("create informatie-aanvraag for this agency");
            }
            if (dossier.agencyId !== agencyId) {
                throw new AgencyMismatch();
            }
            const provider = await tx.run(zql.hdnProvider.where("name", providerCode).one());
            if (!provider?.receiverCode) {
                throw new Error("Invalid provider code");
            }
            const lenderConnection = await tx.run(zql.hdnLenderConnection
                .where("agencyId", dossier.agencyId)
                .where("lenderCode", providerCode)
                .related("serviceProvider", (cb) => cb.related("connections", (cb) => cb.where("agencyId", dossier.agencyId)))
                .one());
            // let connectionNumber: string | undefined;
            let serviceProviderId;
            if (lenderConnection) {
                if ((lenderConnection.sendMethod === "direct" ||
                    !lenderConnection.sendMethod) &&
                    lenderConnection.connectionNumber) {
                    // connectionNumber = lenderConnection.connectionNumber;
                }
                else if (lenderConnection.sendMethod === "service_provider" &&
                    lenderConnection.serviceProviderId) {
                    const serviceProvider = lenderConnection.serviceProvider;
                    const agencyConnection = serviceProvider?.connections[0];
                    if (agencyConnection?.connectionNumber) {
                        // connectionNumber = agencyConnection.connectionNumber;
                        serviceProviderId = agencyConnection.serviceProviderId;
                    }
                }
            }
            await tx.mutate.hdnDossierTable.insert({
                id: hdnDossierId,
                serviceProviderId,
                receiverCode: provider.receiverCode,
                providerCode,
                hdnRequestTraceNr: requestTraceNr,
                dossierId,
                agencyId,
                createdAt,
                kind: "informatie_aanvraag",
            });
            await tx.mutate.hdnInformatieAanvraagTable.insert({
                content,
                id,
                existingMortgageId: existingMortgageId ?? null,
                hdnDossierId,
                dossierId,
                agencyId,
                createdAt,
            });
        }),
    },
});
